import {usePageStore} from "@/stores/page"
import {storeToRefs} from "pinia"
import {useDialog} from "primevue/usedialog"
import {provide, reactive, ref, watch} from "vue"
import dataTestId from "@/constants/dataTestId"

export interface DialogInstance {
  data: any
  close: () => void
  update?: (config: {props: Record<string, any>; data: Record<string, any>}) => void
}

export function useDynamicDialog() {
  const dialog = useDialog()
  const activeDialog = ref<DialogInstance | null>(null)
  provide("DialogRef", activeDialog)

  const pageStore = usePageStore()
  const {isMobile} = storeToRefs(pageStore)

  // Default props for all dialogs
  const defaultProps = {
    modal: true,
    dismissableMask: true,
    closable: true,
    closeOnEscape: true,
    maximizable: false,
    draggable: false,
    pt: {
      header: {
        class: "bordered-dialog-header",
      },
      root: {
        class: "",
      },
      pcCloseButton: {
        root: {
          "data-testid": dataTestId.modals.common.closeButton,
        },
      },
    },
  }

  let stopWatching: (() => void) | null = null // Store the cleanup function for the watcher

  /**
   * Opens a dynamic dialog.
   * @param component - The Vue component to render inside the dialog.
   * @param config - Configuration object containing props, slots, and events.
   */
  const openDialog = (
    component: any,
    config: {
      props?: Record<string, any>
      templates?: Record<string, any>
      data?: Record<string, any>
      slots?: Record<string, Function>
      emits?: Record<string, Function>
      events?: Record<string, Function>
    } = {}
  ) => {
    const {props = {}, templates = {}, slots = {}, data = {}, emits = {}, events = {}} = config // Destructure config

    if (props.maximized === true && isMobile.value) {
      defaultProps.pt.root.class = "p-dialog-maximized"
    } else {
      defaultProps.pt.root.class = ""
    }
    const reactiveProps = reactive({...props, ...defaultProps})

    activeDialog.value = dialog.open(component, {
      props: {...defaultProps, ...reactiveProps}, // Merge defaults with custom props
      templates,
      slots,
      data,
      emits,
      // Bind custom events like 'onClose' to the dialog instance
      ...events,
    })

    // Cleanup any previous watcher
    if (stopWatching) {
      stopWatching()
    }

    stopWatching = watch(isMobile, (newV) => {
      console.log("activeDialog.value", activeDialog.value)
      // @ts-ignore
      if (activeDialog.value && activeDialog.value.options.props?.maximized === true) {
        // @ts-ignore
        if (newV) {
          // @ts-ignore
          activeDialog.value.options.props.pt.root.class = "p-dialog-maximized"
        } else {
          // @ts-ignore
          activeDialog.value.options.props.pt.root.class = ""
        }
      }
    })
  }

  /**
   * Closes the currently open dialog.
   */
  const closeDialog = () => {
    if (activeDialog.value) {
      activeDialog.value.close()
    }
    activeDialog.value = null
    if (stopWatching) {
      stopWatching()
      stopWatching = null
    }
  }

  return {openDialog, closeDialog}
}
